import "./focusList.css";

import { AD_MAX_VISUALIZED_ELEMENTS, HIPHEN_GREEN } from "../../../constants";
import { Badge, Button, Input } from "reactstrap";
import {
  addFocusedVarieties,
  removeFocusedVariety,
  setFavoriteVarieties,
  setFocusedVarieties,
  setVisualizedVarieties,
} from "../../../actions/resultMap";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ComplexSearch } from "../../../components/complexSearch/ComplexSearch";
import { ExpandButton } from "../../../components/expandButton/expandButton";
import { FocusListItem } from "./FocusListItem";
import PropTypes from "prop-types";
import { Virtuoso } from "react-virtuoso";
import { removeElementFromArray } from "../../utils";
import { selectControlVarieties } from "../../../selectors/resultMap";
import { useGetGroupColor } from "../../../hooks/useGetGroupColor";

/* FocusList displays the focused elements selected
 * It is a list group with additionnal controls to
 * interact with other components from the template
 * focusedElements are string names: generic (can be: genotypes / names / treaments ..)
 */
export const FocusList = ({
  groups,
  expandFocusView,
  setExpandFocusView,
  propertyFilter = "",
  setPropertyFilter,
  selectedFeature,
  openPlotRatingModal,
  userCanRatePlots,
  focusPlots,
}) => {
  const controlVarieties = useSelector(selectControlVarieties);
  const [features, focusedVarieties, visualizedVarieties, favoriteVarieties] =
    useSelector(({ resultMap }) => [
      resultMap.features,
      resultMap.focusedVarieties,
      resultMap.visualizedVarieties,
      resultMap.favoriteVarieties,
    ]);

  const dispatch = useDispatch();

  const getColor = useGetGroupColor();

  // For show button: toggle visualisation of element, handles visualised list
  // Size limited to DAHLIA_MAX_VISUALIZED_ELEMENTS
  const handleSwitchVisible = useCallback(
    (gen) => () => {
      if (visualizedVarieties.includes(gen))
        dispatch(
          setVisualizedVarieties(
            removeElementFromArray(visualizedVarieties, gen)
          )
        );
      else if (visualizedVarieties.length < AD_MAX_VISUALIZED_ELEMENTS)
        dispatch(setVisualizedVarieties([...visualizedVarieties, gen]));
    },
    [dispatch, visualizedVarieties]
  );

  const exportToCSV = useCallback(() => {
    const csvContent =
      "data:text/csv;charset=utf-8," + focusedVarieties.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `focused_varieties_${new Date().toISOString()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [focusedVarieties]);

  // Pinned elements to be persistent on clear
  const handleSwitchFavorite = useCallback(
    (gen) => () => {
      if (favoriteVarieties.includes(gen))
        dispatch(
          setFavoriteVarieties(removeElementFromArray(favoriteVarieties, gen))
        );
      else dispatch(setFavoriteVarieties([...favoriteVarieties, gen]));
    },
    [favoriteVarieties, dispatch]
  );

  const handleRemove = useCallback(
    (gen) => () => {
      dispatch(removeFocusedVariety(gen));
    },
    [dispatch]
  );

  // Removes all visualized elements
  const clearVisualisation = () => {
    dispatch(setVisualizedVarieties([]));
  };

  // Removes every element except pinned ones
  const clearList = () => {
    clearVisualisation();
    dispatch(setFocusedVarieties(favoriteVarieties));
  };

  const content = useMemo(
    () => ({
      varieties: {
        options: groups
          .filter((grp) => !focusedVarieties.includes(grp))
          .map((grp) => ({
            label: grp,
            value: grp,
            renderBullet: (
              <i className="fa-solid fa-dna" style={{ color: HIPHEN_GREEN }} />
            ),
          })),
      },
      "Control Varieties": {
        options: controlVarieties
          .filter((grp) => !focusedVarieties.includes(grp))
          .map((grp) => ({
            label: grp,
            value: grp,
            renderBullet: (
              <i className="fa-brands fa-pagelines control-variety" />
            ),
          })),
      },
      plots: {
        // With focusPlots (PCA), a plot is focused individually by its
        // displayId (its single point gets colored on the graph); otherwise
        // selecting a plot focuses its whole variety group (default behaviour).
        options: features
          .filter(
            (feature) =>
              !focusedVarieties.includes(
                focusPlots ? feature.displayId : feature.group
              )
          )
          .map((feature) => ({
            label: feature.displayId,
            value: focusPlots ? feature.displayId : feature.group,
            renderBullet: (
              <i
                className="fa-solid fa-square"
                style={{ color: HIPHEN_GREEN }}
              />
            ),
          })),
      },
    }),
    [features, focusedVarieties, groups, controlVarieties, focusPlots]
  );

  return (
    <div className="powerdash-component focus-list">
      <div className="focus-list-controls">
        <ComplexSearch
          content={content}
          closeOnSelect={false}
          placeholder="Search among varieties and plots..."
          handleClickDefault={(value) => {
            dispatch(addFocusedVarieties([value]));
          }}
          disabled={!groups.length}
          inverted={!expandFocusView}
        />

        <Badge pill className="focused-elements-badge">
          {focusedVarieties.length}
        </Badge>
        {/* Make this component expandable only if the expand function is defined */}
        {setExpandFocusView && (
          <ExpandButton
            expanded={expandFocusView}
            setExpanded={setExpandFocusView}
          />
        )}
      </div>
      <Virtuoso
        data={focusedVarieties}
        computeItemKey={(_, gen) => gen}
        itemContent={(_, gen) => (
          <FocusListItem
            name={gen}
            isControl={controlVarieties.includes(gen)}
            icon={
              controlVarieties.includes(gen)
                ? "fa-brands fa-pagelines control-variety"
                : groups.includes(gen)
                ? "fa-solid fa-dna"
                : "fa-solid fa-square"
            }
            switchVisible={handleSwitchVisible(gen)}
            switchFavorite={handleSwitchFavorite(gen)}
            onRemove={handleRemove(gen)}
            visualIndex={visualizedVarieties.indexOf(gen)}
            isFavorite={favoriteVarieties.includes(gen)}
            color={getColor(gen)}
          />
        )}
      />
      {selectedFeature && !focusedVarieties.includes(selectedFeature.group) && (
        <Badge
          className="focus-list-item-name pending-selection"
          data-tooltip-id="tooltip"
          data-tooltip-content={`Click to add variety of the selected feature on plot map (${selectedFeature.id})`}
          data-tooltip-place="top"
          data-text-color="white"
          onClick={() => dispatch(addFocusedVarieties([selectedFeature.group]))}
        >
          <i className="fa-solid fa-plus"></i> {selectedFeature.group}
        </Badge>
      )}
      <div className="controls">
        <Button
          outline
          className="control-btn icon"
          onClick={clearList}
          disabled={focusedVarieties.length < 1}
          data-tooltip-id="tooltip"
          data-tooltip-content="Remove all genotypes except favorites"
          data-tooltip-place="top"
          data-tooltip-variant="dark"
        >
          <span className="buttontext">
            <i className="fa-solid fa-trash-can" />
          </span>
        </Button>
        <Button
          outline
          className="control-btn icon"
          onClick={clearVisualisation}
          disabled={visualizedVarieties.length < 1}
          data-tooltip-id="tooltip"
          data-tooltip-content="Clear thumbnail views"
          data-tooltip-place="top"
          data-tooltip-variant="dark"
        >
          <span className="buttontext">
            <i className="fa-solid fa-eye-slash"></i>
          </span>
        </Button>
        <Button
          className="control-btn icon"
          onClick={exportToCSV}
          disabled={focusedVarieties.length < 1}
          data-tooltip-id="tooltip"
          data-tooltip-content="Export focused varieties"
          data-tooltip-place="top"
          data-tooltip-variant="dark"
        >
          <span className="buttontext">
            <i className="fa-solid fa-download"></i>
          </span>
        </Button>
        {userCanRatePlots && openPlotRatingModal && (
          <Button
            className="control-btn"
            style={{ backgroundColor: HIPHEN_GREEN, border: "none" }}
            disabled={focusedVarieties.length < 1}
            onClick={openPlotRatingModal}
          >
            <span className="buttontext">
              <i className="fa-solid fa-pencil" /> Rate plots
            </span>
          </Button>
        )}
        {setPropertyFilter && (
          <Input
            className="search-property"
            bsSize="sm"
            placeholder="Search property"
            value={propertyFilter}
            disabled={visualizedVarieties.length === 0}
            onChange={(event) => setPropertyFilter(event.target.value)}
          />
        )}
      </div>
    </div>
  );
};

FocusList.propTypes = {
  groups: PropTypes.array.isRequired,
  expandFocusView: PropTypes.bool,
  setExpandFocusView: PropTypes.func,
  propertyFilter: PropTypes.string,
  selectedFeature: PropTypes.object,
  openPlotRatingModal: PropTypes.func,
  userCanRatePlots: PropTypes.bool,
  setPropertyFilter: PropTypes.func,
  focusPlots: PropTypes.bool,
};
